import { addNewMeditatorRequestSchema } from "@/constants/zod-schemas";
import { useGetMeditationRetreatLevels } from "@/hooks/query-hooks/useLevels";
import { Form, FormInstance, FormProps, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { NRCInput } from "../common";

export type MeditatorFormValues = {
	bookNo: string;
	levelId: number;
	name: string;
	nrc: string;
	phone: string;
	dob: dayjs.Dayjs | null;
	address: string;
	email: string | null;
	nrc_number: string;
	fatherName: string;
	note: string | null;
};

type MeditatorFromProps = {
	form: FormInstance;
	onSubmit: (values: AddNewMeditatorRequest) => void;
	formName: string;
} & Omit<FormProps, "onFinish" | "name" | "form">;

export function MeditatorForm({
	form,
	onSubmit,
	formName,
	...props
}: MeditatorFromProps) {
	const { data: levels } = useGetMeditationRetreatLevels();

	const onFinish = (values: MeditatorFormValues) => {
		const data = {
			...values,
			nrcNo: values?.nrc_number === "" ? null : values?.nrc,
		};

		const payload: AddNewMeditatorRequest =
			addNewMeditatorRequestSchema.parse(data);

		onSubmit(payload);
	};

	const phone = Form.useWatch("phone", form);
	const email = Form.useWatch("email", form);

	// Validate phone and email fields, clear the other field if one is filled
	useEffect(() => {
		const isEmailTouched = form.isFieldTouched("email");
		const isPhoneTouched = form.isFieldTouched("phone");

		if (isEmailTouched || isPhoneTouched) {
			form.validateFields(["phone", "email"]);
		}
	}, [phone, email, form]);

	return (
		<Form
			form={form}
			name={formName}
			onFinish={onFinish}
			layout="vertical"
			className="!pt-4"
			{...props}
			scrollToFirstError
		>
			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name={"bookNo"}
					label="စာအုပ်အမှတ်"
					rules={[
						{
							required: true,
							message: "Required!",
						},
					]}
				>
					<Input size="large" placeholder="စာအုပ်အမှတ်" />
				</Form.Item>

				<Form.Item
					name={"levelId"}
					label="စာအုပ်အဆင့်"
					rules={[
						{
							required: true,
							message: "Required!",
						},
					]}
				>
					<Select
						size="large"
						placeholder="စာအုပ်အဆင့်"
						options={levels?.map((level) => ({
							label: level.name,
							value: level.id,
						}))}
					/>
				</Form.Item>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name={"name"}
					label="အမည်"
					rules={[
						{
							required: true,
							message: "Required!",
						},
					]}
				>
					<Input size="large" placeholder="အမည်" />
				</Form.Item>
				<Form.Item
					name={"dob"}
					label="မွေးနေ့"
					rules={[
						{
							required: true,
							message: "Required!",
						},
					]}
				>
					<Input.TextArea
						className="w-full"
						size="large"
						autoSize={{
							maxRows: 1,
						}}
						placeholder="မွေးနေ့"
					/>
				</Form.Item>
			</div>
			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name={"phone"}
					label="ဖုန်းနံပါတ်"
					rules={[
						{
							required: !email,
							message: "ဖုန်းနံပါတ် (or) Email တစ်ခုကို ဖြည့်ပါ။",
						},
						{
							pattern: /^\d{6,15}$/,
							message: "Invalid phone number format!",
						},
					]}
				>
					<Input size="large" placeholder="ဖုန်းနံပါတ်" />
				</Form.Item>

				<Form.Item
					name={"email"}
					label="Email"
					rules={[
						{
							required: !phone,
							message: "ဖုန်းနံပါတ် (or) Email တစ်ခုကို ဖြည့်ပါ။",
						},
						{
							type: "email",
							message: "Invalid Email address!",
						},
					]}
				>
					<Input size="large" placeholder="Email" />
				</Form.Item>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					label="အဖ အမည်"
					name="fatherName"
					rules={[
						{
							required: true,
							message: "Required!",
						},
					]}
				>
					<Input size="large" placeholder="အဖ အမည်" />
				</Form.Item>

				<NRCInput form={form} />
			</div>

			<Form.Item
				name={"address"}
				label="နေရပ်လိပ်စာ"
				rules={[
					{
						required: true,
						message: "Required!",
					},
				]}
			>
				<Input.TextArea
					rows={2}
					className="!resize-none"
					placeholder="နေရပ်လိပ်စာ"
					size="large"
				/>
			</Form.Item>
			<Form.Item name={"note"} label="Note">
				<Input.TextArea
					rows={2}
					className="!resize-none"
					placeholder="Note"
					size="large"
				/>
			</Form.Item>
		</Form>
	);
}
